<template>
  <help-dialog title="About Workflows" content-class="dialog-top-right">
    <template #activator="{ openDialog }">
      <v-btn
        variant="text"
        icon="mds:help"
        class="mr-4"
        @click="openDialog"
      />
    </template>

    <template #default>
      <h3 class="mb-1 text-black">Workflow</h3>
      <p class="text-grey-darken-3">
        A workflow is a sequence of <b>Events</b> arranged on a timeline. It supports various approaches, such as:
      </p>
      <br>
      <ol class="pl-10 text-black">
        <li>
          <a
            href="https://www.qlerify.com/event-storming-tool"
            target="_blank"
            class="text-blue-accent-4"
          >
            <b>Event Storming</b>
          </a>
        </li>
        <li>
          <a
            href="https://www.qlerify.com/event-modeling-tool"
            target="_blank"
            class="text-blue-accent-4"
          >
            <b>Event Modeling</b>
          </a>
        </li>
        <li>
          <a
            href="https://www.qlerify.com/process-mapping-tool"
            target="_blank"
            class="text-blue-accent-4"
          >
            <b>Simple Process Mapping</b>
          </a>
        </li>
        <li>
          <a
            href="https://www.qlerify.com/user-story-mapping"
            target="_blank"
            class="text-blue-accent-4"
          >
            <b>User Story Mapping</b>
          </a>
        </li>
        <li>
          <a
            href="https://www.qlerify.com/domain-driven-design-tool"
            target="_blank"
            class="text-blue-accent-4"
          >
            <b>Domain Driven Design (DDD)</b>
          </a>
        </li>
      </ol>
      <br>
      Click on the links above to learn more about each approach.
      <br><br>

      <h3 class="mb-1 text-black">Events</h3>
      <p class="text-grey-darken-3">
        An <b>Event</b> is something important that happens in your organization—typically
        described with a past-tense verb and a noun like "Order Created" or "Broken Equipment
        Detected." Events can be triggered by people (internal or external), systems, or time.
        <br><br>
        We recommend capturing only <b>state-changing</b> events—those that changes information in a system.
        For example, "Guest viewed calendar for room availability" is <b>not</b> considered an event unless it changes the system's state.
        <br><br>
        Events might also be referred to as steps in a process, tasks in a user journey, or Scrum epics.
        <br><br>
        You can add events by clicking between existing events or using the <b>+ Add start point</b> button
        to insert an event at the beginning of the workflow timeline.
        <br><br>
        <b>Multi-event selection:</b> Switch to <b>Pointer mode</b> using the toolbar button above the diagram,
        then drag a selection box around multiple events. Once selected, you can move, copy, cut, paste, delete,
        or change the color of all selected events at once. Use <b>Hand mode</b> to return to panning the diagram.
      </p>

      <br><br>

      <h3 class="mb-1 text-black">Lanes</h3>
      <p class="text-grey-darken-3">
        Lanes indicate the primary <b>Actor</b> for each <b>Event</b> in the workflow.
        An Actor (such as a user or a system) initiates a <b>Command</b> that leads to an Event.
        For example, in "Customer created order," the Actor is "Customer," and "created order" is the Event.
        <br><br>
        You can add lanes using the <b>+ Add lane</b> button located below the diagram,
        or by using the three-dot menu that appears at the top left of each lane when you hover over it.
        <br><br>
        To delete a lane, use the three-dot menu (top left of the lane when hovering), but note that
        you can only delete a lane when it is empty (contains no events).
      </p>

      <br><br>

      <h3 class="mb-1 text-black">Groups</h3>
      <p class="text-grey-darken-3">
        The workflow allows you to organize Events into <b>Groups</b> to represent phases, milestones, or other meaningful segments.
        Click the <b>+Add group</b> button below the diagram to start placing a group. A grey dashed line follows your cursor —
        click where the new group should start, or press <b>Esc</b> to cancel. The first group you place splits the diagram in two,
        so you can then drag and drop Events between the groups and drag the dividers sideways to adjust them.
      </p>

      <br><br>

      <h3 class="mb-1 text-black">Cards</h3>
      <p class="text-grey-darken-3">
        Cards let you add details—such as user stories, test cases, problems, or opportunities—to each Event.
        You can add multiple Cards to an Event, and each Card can include comments, attached files, and reactions.
        <br><br>
        Use <b>workflow settings</b> (via the tool icon <v-icon size="x-small">mdi-cog-outline</v-icon> at the top right) to configure available Card types.
        Cards of card types marked with <b>“Show in backlog”</b> appear in the backlog, where they can be reordered to reflect priority.
        <br><br>
        There are 4 special card types used to build the domain model:
      </p>
      <br>
      <ol class="pl-10 text-black">
        <li><b>Command:</b> The name of the command an Actor initiates</li>
        <li><b>Aggregate Root:</b> The name of the Aggregate Root for this command</li>
        <li><b>Read Model:</b> The name of the query that provides information to the actor</li>
        <li><b>Given-When-Then:</b> A card to capture acceptance criteria in the form of Given-When-Then</li>
      </ol>
      <br>
      An Event can have <b>multiple</b> Read Models, but only <b>one</b> Command and <b>one</b> Aggregate Root.

      <br><br>

      <h3 class="mb-1 text-black">Backlog & User Story Map</h3>
      <p class="text-grey-darken-3">
        There are two ways to work with the backlog, both located under the workflow:
      </p>
      <br>
      <ol class="pl-10 text-black">
        <li><b>Backlog –</b> Shows all cards, that are configured to be shown in the backlog, in order of priority.</li>
        <li><b>User Story Map –</b> Cards are displayed in a horizontal layout aligned with their corresponding events.</li>
      </ol>
      <br>
      <p class="text-grey-darken-3">
        Both views display only Cards belonging to Card types configured as <b>“Show in backlog”</b>.
        You can assign releases by selecting a number in the <b>Release</b> column in <b>Backlog</b>
        or by hovering over a Card and using the context menu in the <b>User Story Map</b>. Or by using drag and drop.
      </p>

      <br><br>

      <h3 class="mb-1 text-black">Filters</h3>
      <p class="text-grey-darken-3">
        Apply visual filters to the entire workflow to highlight specific events.
        Use the <b>Filter</b> dropdowns above the workflow to filter by:
      </p>
      <br>
      <ul class="pl-10 text-grey-darken-3">
        <li><b>Release:</b> Select which releases you want to view, to highlight the scope for each iteration.</li>
        <li><b>Tag:</b> If you have a card type with the <b>Tag</b> domain model role, you can filter events by their tag values.</li>
      </ul>

      <br><br>

      <h3 class="mb-1 text-black">Domain Model</h3>
      <p class="text-grey-darken-3">
        The <b>Domain Model</b> section, located under the workflow, allows you to visualize your domain model and group <b>Entities</b> into <b>Bounded Contexts</b>.
        This section includes detailed help texts to guide you through the process.
      </p>

      <br><br>

      <h3 class="mb-1">History</h3>
      <p class="text-grey-darken-3">
        The History section displays a record of the latest updates made to the workflow and allows you to roll back to previous versions.
      </p>

      <br><br>

      <h3 class="mb-1">AI</h3>
      <p class="text-grey-darken-3">
        Use AI to quickly create any kind of workflow, enriched with the cards of your choice.
        AI can either create a workflow from scratch or add individual steps, cards, comments, read models and commands to an existing workflow.
        To create a complete workflow with AI, start from an empty workflow and click the "<b>Generate Workflow with AI</b>" button.
      </p>

      <br><br>

      <h3 class="mb-1">Exports</h3>
      <p class="text-grey-darken-3">
        You can export your workflow in several formats to share, backup, or integrate with other tools.
        Access the export options via the export button <v-icon size="x-small">mds:cloud-download</v-icon> at the top right of the workflow.
      </p>
      <br>
      <ol class="pl-10 text-black">
        <li>
          <b>Export CSV</b> – Exports workflow cards and their details (including backlog priority, lanes, events, card types, releases, and comments) to a CSV file for analysis in spreadsheet applications.
        </li>
        <li>
          <b>Export Spec</b> – Exports the workflow as a compact, domain-centric specification (OpenAPI-like JSON) scoped to a bounded context. Useful for sharing with AI agents, programmatic access, or data integration. Can be imported back using the Import option.
        </li>
        <li>
          <b>Export Workflow</b> – Saves the complete workflow (including events, cards, card types, and bounded contexts) as an encrypted .qlerify file. Can be imported back using the Import option.
        </li>
        <li>
          <b>Export PDF</b> – Generates a PDF document of the workflow diagram, ideal for presentations or documentation.
        </li>
        <li>
          <b>Export Applicon</b> – Available when enabled in workflow settings, exports workflow data to Applicon format.
        </li>
        <li>
          <b>Export to Jira</b> – Transfers cards to Jira after mapping your Continuum Design project to a Jira space. Requires installing the Qlerify app from the Atlassian Marketplace.
        </li>
        <li>
          <b>Export to Azure DevOps</b> – Exports workflow data to Azure DevOps after mapping your Continuum Design project. Requires installing the Qlerify app from the Visual Studio Marketplace.
        </li>
      </ol>

      <br><br>

      <h3 class="mb-1">Import</h3>
      <p class="text-grey-darken-3">
        You can access the import option via the <v-icon size="x-small">mds:cloud-download</v-icon> button at the top right of the workflow.
        The workflow must be empty to import. Three formats are supported:
      </p>
      <br>
      <ul class="pl-10 text-grey-darken-3">
        <li><b>.qlerify</b> – Exported workflow files</li>
        <li><b>JSON</b> – Exported JSON files</li>
        <li>
          <b>JSON Schema Specification</b> – The workflow specification format available in the <b>Push Spec</b> tab on the Domain Model page
        </li>
      </ul>
    </template>
  </help-dialog>
</template>

<script setup>
import HelpDialog from '@/components/HelpDialog.vue'
</script>
